import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/ui';
import {
  GetServiceTypes, GetServicesByEventId, AddService, UpdateService, DeleteService,
  GetFunctionsByEventId, GetAllUsers,
} from '../../api/api';
import { fmtDate, fmtTime, normaliseText, money } from '../../utils/helpers';

export const RATE_TYPES = [
  { value: 'PerPlate', label: 'Per plate' },
  { value: 'Fixed', label: 'Fixed amount' },
  { value: 'PerDay', label: 'Per day' },
  { value: 'PerHour', label: 'Per hour' },
];
export const SERVICE_STATUSES = ['Enquiry', 'Confirmed', 'Done', 'Cancelled'];
export const OTHER_SERVICE_TYPE = 'Other';
export const isPerUnit = (rateType) => ['PerPlate', 'PerDay', 'PerHour'].includes(String(rateType || ''));

const ten = (v) => String(v || '').replace(/\D/g, '').slice(0, 10);
const emptyToNull = (obj) => Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, v === '' ? null : v]));
const blankForm = () => ({
  ServiceTypeId: '', ServiceType: '', FunctionId: '',
  VendorUserId: '', VendorMobileNumber: '', VendorName: '', CompanyName: '',
  ContactName: '', ContactMobile: '',
  RateType: 'Fixed', Rate: '', ConfirmedCount: '', TotalAmount: '', AdvancePaid: '',
  ServiceDate: new Date().toISOString().slice(0, 10), StartTime: '', EndTime: '', Status: 'Enquiry', Notes: '',
});

// All Services logic — shared by Services.desktop / Services.mobile.
// One generic module: catering, event management, DJ, priest … differ only by
// their ServiceType row, so the same form and list serve them all.
export function useServices(event) {
  const { user, organiser, isAdmin, can } = useAuth();
  const toast = useToast();
  const me = user.UserId;

  const canView = isAdmin || can('CanServices') || can('CanArrangements');
  const canAdd = isAdmin || can('CanServicesAdd') || can('CanServices');
  const canEdit = isAdmin || can('CanServicesEdit') || can('CanServices');
  const canDelete = isAdmin || can('CanServicesDelete') || can('CanServices');

  const [rows, setRows] = useState([]);
  const [types, setTypes] = useState([]);
  const [functions, setFunctions] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blankForm);
  const [saving, setSaving] = useState(false);
  // 'existing' = pick a user who already has an account, 'new' = create one.
  const [vendorMode, setVendorMode] = useState('new');

  const refresh = useCallback(() => {
    setLoading(true);
    GetServicesByEventId({ EventId: event.EventId })
      .then(d => setRows(Array.isArray(d) ? d : []))
      .catch(() => toast('Failed to load services'))
      .finally(() => setLoading(false));
  }, [event, toast]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    GetServiceTypes().then(d => setTypes(Array.isArray(d) ? d : [])).catch(() => setTypes([]));
    GetAllUsers().then(d => setUsers(Array.isArray(d) ? d : [])).catch(() => setUsers([]));
  }, []);
  useEffect(() => {
    GetFunctionsByEventId({ EventId: event.EventId })
      .then(d => setFunctions(Array.isArray(d) ? d : [])).catch(() => setFunctions([]));
  }, [event]);

  // Options
  const typeOptions = useMemo(() => [
    ...types.map(t => ({ value: String(t.ServiceTypeId), label: t.ServiceTypeName })),
    { value: OTHER_SERVICE_TYPE, label: 'Other (type a new one)' },
  ], [types]);
  const functionOptions = useMemo(() => [
    { value: '', label: 'Whole event (all functions)' },
    ...functions.map(f => ({
      value: String(f.FunctionId),
      label: `${f.FunctionName} · ${fmtDate(f.FunctionDate)}${f.StartTime ? ` ${fmtTime(f.StartTime)}` : ''}`,
    })),
  ], [functions]);
  const userOptions = useMemo(() => users
    .filter(u => Number(u.Valid) !== 0)
    .map(u => ({ value: String(u.UserId), label: `${[u.Surname, u.Name].filter(Boolean).join(' ')} · ${u.MobileNumber || ''}` })), [users]);

  const filtered = useMemo(() => {
    let list = rows;
    if (typeFilter) list = list.filter(r => String(r.ServiceTypeId) === String(typeFilter));
    if (q) {
      const s = q.toLowerCase();
      list = list.filter(r => JSON.stringify(r).toLowerCase().includes(s));
    }
    return list;
  }, [rows, typeFilter, q]);

  // Money at a glance for the whole event.
  const totals = useMemo(() => filtered.reduce((acc, r) => ({
    total: acc.total + Number(r.TotalAmount || 0),
    advance: acc.advance + Number(r.AdvancePaid || 0),
    balance: acc.balance + Number(r.BalanceAmount || 0),
    plates: acc.plates + (isPerUnit(r.RateType) ? Number(r.ConfirmedCount || 0) : 0),
  }), { total: 0, advance: 0, balance: 0, plates: 0 }), [filtered]);

  // Services grouped by day, in the order the days run (several functions a day).
  const byDay = useMemo(() => {
    const map = new Map();
    filtered.forEach(r => {
      const key = String(r.FunctionDate || r.ServiceDate || '').slice(0, 10) || 'nodate';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    });
    return Array.from(map.entries())
      .sort((a, b) => (a[0] === 'nodate' ? 1 : b[0] === 'nodate' ? -1 : a[0].localeCompare(b[0])))
      .map(([date, items]) => ({ date: date === 'nodate' ? '' : date, items }));
  }, [filtered]);

  const selectedType = useMemo(
    () => types.find(t => String(t.ServiceTypeId) === String(form.ServiceTypeId)) || null,
    [types, form.ServiceTypeId]
  );

  const setF = (k, v) => setForm(f => {
    const next = { ...f, [k]: ['CompanyName', 'ContactName', 'VendorName'].includes(k) ? normaliseText(v) : v };
    if (k === 'ContactMobile' || k === 'VendorMobileNumber') next[k] = ten(v);
    // Picking a type brings its default rate type with it.
    if (k === 'ServiceTypeId') {
      const t = types.find(x => String(x.ServiceTypeId) === String(v));
      if (t?.RateType) next.RateType = t.RateType;
      if (v !== OTHER_SERVICE_TYPE) next.ServiceType = t?.ServiceTypeName || '';
    }
    // Per-unit rates compute the total; a fixed amount is typed directly.
    if (['Rate', 'ConfirmedCount', 'RateType'].includes(k) && isPerUnit(next.RateType)) {
      const total = Number(next.Rate || 0) * Number(next.ConfirmedCount || 0);
      next.TotalAmount = total ? String(total) : next.TotalAmount;
    }
    return next;
  });

  // `prefill` lets another screen open this form ready-made - the Edit
  // Function form passes the function, its date and its slot.
  const openAdd = (prefill = {}) => {
    setEditing(null);
    setForm({ ...blankForm(), FunctionId: '', ...Object.fromEntries(Object.entries(prefill).filter(([, v]) => v !== undefined)) });
    setVendorMode('new');
    setOpen(true);
  };
  const openEdit = (row) => {
    setEditing(row);
    setForm({
      ServiceTypeId: row.ServiceTypeId ? String(row.ServiceTypeId) : '',
      ServiceType: row.ServiceType || '',
      FunctionId: row.FunctionId ? String(row.FunctionId) : '',
      VendorUserId: row.VendorUserId ? String(row.VendorUserId) : '',
      VendorMobileNumber: '', VendorName: '',
      CompanyName: row.CompanyName || '',
      ContactName: row.ContactName || '', ContactMobile: row.ContactMobile || '',
      RateType: row.RateType || 'Fixed',
      Rate: row.Rate != null ? String(row.Rate) : '',
      ConfirmedCount: row.ConfirmedCount != null ? String(row.ConfirmedCount) : '',
      TotalAmount: row.TotalAmount != null ? String(row.TotalAmount) : '',
      AdvancePaid: '',                       // payments are added in the detail page
      ServiceDate: String(row.ServiceDate || '').slice(0, 10) || new Date().toISOString().slice(0, 10),
      StartTime: row.StartTime || '', EndTime: row.EndTime || '',
      Status: row.Status || 'Enquiry', Notes: row.Notes || '',
    });
    setVendorMode(row.VendorUserId ? 'existing' : 'new');
    setOpen(true);
  };

  const save = async () => {
    const typeName = form.ServiceTypeId === OTHER_SERVICE_TYPE
      ? String(form.ServiceType || '').trim()
      : (selectedType?.ServiceTypeName || form.ServiceType);
    if (!typeName) return toast('Pick the type of service');
    if (!form.CompanyName && !form.ContactName && !form.VendorName) return toast('Company or owner name is required');
    // A service with no date shows up nowhere in the timetable; a service tied
    // to a function takes that function's date instead.
    if (!form.FunctionId && !form.ServiceDate) return toast('Date is required');

    const payload = emptyToNull({
      ...form,
      ServiceTypeId: form.ServiceTypeId === OTHER_SERVICE_TYPE ? '' : form.ServiceTypeId,
      ServiceType: typeName,
      VendorUserId: vendorMode === 'existing' ? form.VendorUserId : '',
      VendorMobileNumber: vendorMode === 'new' ? (form.VendorMobileNumber || form.ContactMobile) : '',
      VendorName: vendorMode === 'new' ? (form.VendorName || form.ContactName) : '',
      Rate: Number(form.Rate || 0),
      ConfirmedCount: Number(form.ConfirmedCount || 0),
      TotalAmount: Number(form.TotalAmount || 0),
      AdvancePaid: Number(form.AdvancePaid || 0),
    });
    setSaving(true);
    try {
      if (editing) {
        const { AdvancePaid, ...data } = payload;   // advance only changes via payments
        await UpdateService({ data: { ...data, UpdatedBy: me }, where: `ServiceId = ${editing.ServiceId}` });
        toast('Service updated');
      } else {
        await AddService({
          ...payload,
          EventId: event.EventId, OrganiserId: organiser.OrganiserId,
          Valid: 1, CreatedBy: me, UpdatedBy: me,
        });
        toast('Service added');
      }
      setOpen(false); refresh();
    } catch (err) {
      toast(err?.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const del = async (row) => {
    if (!window.confirm(`Remove ${row.ServiceType} — ${row.CompanyName || row.ContactName || ''}?`)) return;
    try {
      await DeleteService({ where: `ServiceId = ${row.ServiceId}` });
      toast('Removed'); refresh();
    } catch { toast('Delete failed'); }
  };

  // Quick status change from the list (Enquiry → Confirmed → Done).
  const setStatus = async (row, Status) => {
    setRows(rs => rs.map(r => (r.ServiceId === row.ServiceId ? { ...r, Status } : r)));
    try {
      await UpdateService({ data: { Status, UpdatedBy: me }, where: `ServiceId = ${row.ServiceId}` });
    } catch { toast('Could not update status'); refresh(); }
  };

  const fnName = useCallback((id) => functions.find(f => String(f.FunctionId) === String(id))?.FunctionName || 'Whole event', [functions]);

  return {
    canView, canAdd, canEdit, canDelete, isAdmin,
    rows, filtered, byDay, totals, loading, q, setQ, typeFilter, setTypeFilter,
    types, typeOptions, functions, functionOptions, userOptions, selectedType,
    open, setOpen, editing, form, setF, saving, openAdd, openEdit, save, del, setStatus,
    vendorMode, setVendorMode, fnName, money, event,
  };
}
